import { BuyerCustomerDTO, PaymentDTO, PaymentMethodDTO, Permission, Res, SaleDTO } from 'core/api';
import { InvalidPaymentError, Result, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { CashdrawerFacade, DeliveryNoteFacade, SalesOrderFacade } from 'pos/facades';
import { showCloseDocumentScreen, ConfirmDeliveryNoteDataScreen, ConfirmSalesOrderDataScreen, makeTicketReadyToClose, blockTicketWhileClosing } from 'pos/modules/helpers/document-creation';
import { ExternalPaymentsService, WhenPrintService } from 'pos/services';
import { PaymentDTOFactory } from 'pos/modules/helpers/payments-processing';
import * as SalesStore from '../SalesStore';
import { Message, Modal } from 'core/ui-next/modal';

async function closeAsInvoice(): Promise<Result<SaleDTO>> {
	return showCloseDocumentScreen({
		layout: 'CloseTicket',
		printDocument: SessionProvider.hasPermission(Permission.Ticket_PrintCopy) ? () => SalesStore.printTicket('IGNORE_TICKET_PRINTED_FLOW') : undefined,
		getDocument: () => {
			const ticket = SalesStore.getTicket();
			return {
				ticketIds: [ticket.Id],
				ticketGlobalId: ticket.GlobalId,
				customer: ticket.Customer,
				pendingAmount: ticket.PendingAmount,
				discounts: {
					cashDiscount: ticket.CashDiscount,
					discountPercentageInCash: ticket.DiscountPercentageInCash,
					discountPercentage: ticket.DiscountPercentage
				}
			};
		},
		setCustomer: SalesStore.setCustomer,
		closeDocument: async params => {

			const checkResult = await SalesStore.checkRequiredCustomer(params.Payments);
			if (!checkResult.IsValid) {
				await Message.show(checkResult.ErrorMessage);
				return Result.error(new InvalidPaymentError(checkResult.ErrorMessage));
			}

			return await SalesStore.closeTicket(params);
		},
	});
}

async function closeAsRefund(): Promise<Result<SaleDTO>> {
	return showCloseDocumentScreen({
		layout: 'CreateRefund',
		// Necesito el Math.abs porque puede haber devoluciones de dos tipos:
		// - Tengo un ticket en positivo y le doy al botón de hacer devolución
		// - Tengo un ticket con líneas en positivo y negativo, y al cerrar ofrezco hacer la devolución
		// En el segundo caso NetAmount es negativo, pero la pantalla trabaja siempre con
		// cantidades positivas y es el servidor el que invierte el signo.
		getDocument: () => {
			const ticket = SalesStore.getTicket();
			return {
				ticketIds: [ticket.Id],
				ticketGlobalId: ticket.GlobalId,
				pendingAmount: Math.abs(ticket.PendingAmount),
				customer: ticket.Customer,
				discounts: {
					cashDiscount: ticket.CashDiscount,
					discountPercentageInCash: ticket.DiscountPercentageInCash,
					discountPercentage: ticket.DiscountPercentage
				}
			};
		},
		setCustomer: SalesStore.setCustomer,
		closeDocument: async params => {

			const checkResult = await SalesStore.checkRequiredCustomer(params.Payments);
			if (!checkResult.IsValid) {
				await Message.show(checkResult.ErrorMessage);
				return Result.error(new InvalidPaymentError(checkResult.ErrorMessage));
			}

			return await SalesStore.refundTicket(params);
		},
	});
}

const getCurrentTicketPendingAmount = () => Math.abs(SalesStore.getTicket().PendingAmount);

async function closeAsDeliveryNote(): Promise<Result<SaleDTO>> {

	const isRefund = SalesStore.getTicket().PendingAmount < 0;
	const draft = await DeliveryNoteFacade.getDraftByTicketId(SalesStore.getTicket().Id);
	const paymentOperation = await CashdrawerFacade.createPaymentOperation();
	const result = await Modal.show(
		ConfirmDeliveryNoteDataScreen,
		{
			isRefund,
			customer: SalesStore.getTicket().Customer,
			deliveryAddress: draft.DeliveryAddress,
			notes: draft.Notes,
			getPendingAmount: getCurrentTicketPendingAmount,
			maxAvailableChange: paymentOperation.MaxAvailableChange,
			paymentMethods: paymentOperation.Methods,
			onCustomerChanged: (customer: BuyerCustomerDTO) => SalesStore.setCustomer(customer)
		});

	if (!result.ok)
		return Result.error();


	const printDocument = await WhenPrintService.shouldPrintDeliveryNote();

	const externalPayments = isRefund ? ExternalPaymentsService.forRefund('PreRefund', SalesStore.getTicket().GlobalId) : ExternalPaymentsService.forPay('PrePay', SalesStore.getTicket().GlobalId);

	const transactionResult = await doTransaction(externalPayments, result.value.method, result.value.prepayAmount, result.value.paidAmount);
	if (!transactionResult.ok)
		return Result.error();

	const createDeliveryNoteResult = await SalesStore.createDeliveryNote({
		Reason: result.value.notes,
		ShouldPrint: printDocument,
		Payments: [transactionResult.value],
		PrepayAmount: result.value.prepayAmount,
		DeliveryAddress: result.value.deliveryAddress,
		DraftSerialNumber: draft.SerialNumber,
		IsRefund: isRefund,
		CustomerDocumentNumber: result.value.customerDocumentNumber,
		RelatedSalesOrders: [],
		InvoiceCanBeEmitted: true
	});

	if (!createDeliveryNoteResult.ok)
		await externalPayments.undoAll();

	return createDeliveryNoteResult;
}

async function closeAsSalesOrder(): Promise<Result<SaleDTO>> {

	if (SalesStore.getTicket().HasPhoneOrder) {
		await Message.show(Res.Msg.MSG_CANNOT_CREATE_ORDER_FROM_SALES_ORDER());
		return Result.error();
	}

	const isRefund = SalesStore.getTicket().PendingAmount < 0;
	const draft = await SalesOrderFacade.getDraftByTicketId(SalesStore.getTicket().Id);
	const customer = SalesStore.getTicket().Customer;
	const paymentOperation = await CashdrawerFacade.createPaymentOperation();

	const result = await Modal.show(
		ConfirmSalesOrderDataScreen,
		{
			isRefund,
			showSchedulePreparation: SessionProvider.isSchedulePreparationEnabled(),
			getPendingAmount: getCurrentTicketPendingAmount,
			onCustomerChanged: (customer: BuyerCustomerDTO) => SalesStore.setCustomer(customer),
			paymentMethods: paymentOperation.Methods,
			maxAvailableChange: paymentOperation.MaxAvailableChange,
			deliveryDate: draft.DeliveryDate,
			deliveryAddress: draft.DeliveryAddress,
			customer,
			notes: draft.Notes,
			schedulePreparation: draft.SchedulePreparation || (draft.SerialNumber.Number === 0 && SessionProvider.isSchedulePreparationEnabled()),
		});

	if (!result.ok)
		return Result.error();

	const printDocument = await WhenPrintService.shouldPrintSalesOrder();

	const externalPayments = isRefund ? ExternalPaymentsService.forRefund('PreRefund', SalesStore.getTicket().GlobalId) : ExternalPaymentsService.forPay('PrePay', SalesStore.getTicket().GlobalId);

	const transactionResult = await doTransaction(externalPayments, result.value.method, result.value.prepayAmount, result.value.paidAmount);
	if (!transactionResult.ok)
		return Result.error();

	const createSalesOrderResult = await SalesStore.createSalesOrder({
		Notes: result.value.notes,
		ShouldPrint: printDocument,
		Payments: [transactionResult.value],
		PrepayAmount: result.value.prepayAmount,
		DeliveryAddress: result.value.deliveryAddress,
		DraftSerialNumber: draft.SerialNumber,
		DeliveryDate: result.value.deliveryDate,
		SchedulePreparation: result.value.schedulePreparation,
		CustomerDocumentNumber: result.value.customerDocumentNumber,
		IsRefund: isRefund,
	});

	if (!createSalesOrderResult.ok)
		await externalPayments.undoAll();

	return createSalesOrderResult;
}


async function doTransaction(externalPayments: ExternalPaymentsService, method: PaymentMethodDTO, prepayAmount: number, paidAmount: number): Promise<Result<PaymentDTO>> {

	// Contra la máquina de efectivo cobramos lo que hay que abonar, no lo entregado. En el resto, cobramos lo que nos han entregado
	const useCashControl = SessionProvider.getCurrent().UseCashControl;
	const payment = PaymentDTOFactory.create(method, method.IsCash && useCashControl ? prepayAmount : paidAmount);
	// Si es efectivo el cambio que pasamos es cero, depende de lo que meta el usuario en la máquina. Si no devuelvo el cambio porque será
	// en otra forma de pago.
	const changeAmount = method.IsCash || method.IsRefundVoucher ? 0 : Math.abs(paidAmount) - Math.abs(prepayAmount);
	const paymentsResult = await externalPayments.register(payment);
	if (!paymentsResult.ok)
		return Result.error();

	const cashResult = await externalPayments.commitCashPayments(changeAmount);
	if (!cashResult.ok)
		return Result.error();

	return Result.ok(payment);
}

async function closeAs(params: CloseCurrentDocumentParams) {
	switch (params.closeAs) {
		case 'Invoice': return closeAsInvoice();
		case 'Refund': return closeAsRefund();
		case 'DeliveryNote': return closeAsDeliveryNote();
		case 'SalesOrder': return closeAsSalesOrder();
	}
}

type CloseCurrentDocumentParams = {
	closeAs: 'DeliveryNote' | 'SalesOrder' | 'Invoice' | 'Refund'
};

declare global {
	interface CommandType {
		closeCurrentTicket: (params: CloseCurrentDocumentParams) => Promise<Result>
	}
}

Command.register('closeCurrentTicket', 'NO_PRECONDITION', async (params: CloseCurrentDocumentParams) => {
	return blockTicketWhileClosing(SalesStore.getTicket().Id, async () => {

		const canBeExecuted = params.closeAs !== 'DeliveryNote' && params.closeAs !== 'SalesOrder' || Preconditions.ensureIsNotLite();

		if (!canBeExecuted)
			return Result.error();

		const result = await makeTicketReadyToClose({
			...params,
			getTicket: SalesStore.getTicket,
			setFriendlyName: SalesStore.setFriendlyName,
			setTableCompanions: SalesStore.setTableCompanions,
		});

		if (!result.ok)
			return Result.error();

		return await closeAs(params);
	});
});
